import { lazy, Suspense, useCallback, useState } from 'react';
import { Flex, Spinner } from '@kognitos/lattice';
import { useIDPDocumentName } from '../hooks';
import { OutputCardWrapper } from './OutputCardWrapper';
import {
  BrowserPreviewStatusBadge,
  BrowserPreviewStatusContext,
} from './BrowserPreviewStatus';
import {
  getOutputIconType,
  isDownloadable,
  isBrowserInstance,
  isPrimitive,
} from '../utils';
import {
  ERenderMode,
  type TBrowserPreviewStatus,
  type TResolvedValue,
} from '../types';

const OutputRenderer = lazy(() =>
  import('./OutputRenderer').then(module => ({
    default: module.OutputRenderer,
  })),
);

const CONFIG_IDP = { disableScroll: true, disableValueTooltip: true } as const;
const CONFIG_NO_TOOLTIP = {
  disableScroll: false,
  disableValueTooltip: true,
} as const;
const CONFIG_DEFAULT = {
  disableScroll: false,
  disableValueTooltip: false,
} as const;
// The 16:9 player sets its own height; the fixed card height left a gap below it.
const CONFIG_BROWSER = {
  disableValueTooltip: true,
  fitContent: true,
} as const;

interface IValuePreviewCardProps {
  name: string;
  value: TResolvedValue;
  displayName?: string;
  onExpand?: (name: string, value: TResolvedValue) => void;
  onDownload?: (name: string, value: TResolvedValue) => void;
}

/**
 * Preserves the established value-preview card used by input chips and inline
 * input values. Output surfaces use the separate sm/md/lg OutputCard system.
 */
export function ValuePreviewCard({
  name,
  value,
  displayName,
  onExpand,
  onDownload,
}: IValuePreviewCardProps) {
  const [scrollArea, setScrollArea] = useState<HTMLDivElement | null>(null);
  const [browserStatus, setBrowserStatus] =
    useState<TBrowserPreviewStatus>(null);
  const iconType = getOutputIconType(value);
  const idpDocument = useIDPDocumentName(value);
  const isIDP = idpDocument !== null;

  const handleExpand = useCallback(() => {
    onExpand?.(name, value);
  }, [name, onExpand, value]);

  const handleDownload = useCallback(() => {
    onDownload?.(name, value);
  }, [name, onDownload, value]);

  const config = isIDP
    ? CONFIG_IDP
    : isBrowserInstance(value)
      ? CONFIG_BROWSER
      : isPrimitive(value)
        ? CONFIG_DEFAULT
        : CONFIG_NO_TOOLTIP;
  const expandHandler = onExpand ? handleExpand : undefined;
  const downloadHandler = idpDocument
    ? idpDocument.download
    : onDownload && isDownloadable(value)
      ? handleDownload
      : undefined;

  return (
    <OutputCardWrapper
      name={name}
      displayName={displayName ?? idpDocument?.displayName}
      horizontal={false}
      iconType={iconType}
      onExpand={expandHandler}
      onDownload={downloadHandler}
      config={config}
      scrollAreaRef={setScrollArea}
      titleAdornment={<BrowserPreviewStatusBadge status={browserStatus} />}
    >
      <BrowserPreviewStatusContext
        value={isBrowserInstance(value) ? setBrowserStatus : null}
      >
        <Suspense
          fallback={
            <Flex align="center" justify="center" className="py-4">
              <Spinner size="sm" />
            </Flex>
          }
        >
          <OutputRenderer
            value={value}
            mode={ERenderMode.PREVIEW}
            outputName={name}
            scrollParent={scrollArea}
            // The card is narrow and has its own expand button; playback
            // controls live in the expanded view.
            minimal
          />
        </Suspense>
      </BrowserPreviewStatusContext>
    </OutputCardWrapper>
  );
}
